import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import DataTable from "../../components/DataTable";
import {
  Card,
  CardBody,
  Spinner,
} from "@material-tailwind/react";
import { toast } from "react-toastify";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";
import {
  SecondaryButton,
  PrimaryButton,
  OutlineButton,
  EditButton,
} from "../../components/Buttons";
import { fetchPropertyConfigurations } from "../../services/projects";
import usePrivilege from "../../hooks/usePrivilege";
import { encryptForTransit } from "../../utils/common";
import AgreementUpload from "../../components/ProjectScreens/AgreementUpload";
import { FormModal, Form, Field, FormSubmit } from "../../components/form";
import * as yup from "yup";

// Inline schema — single field, doesn't merit its own file.
const extensionPeriodSchema = yup.object({
  extensionPeriod: yup
    .number()
    .typeError("Days must be a number")
    .integer("Whole days only")
    .min(1, "Must be at least 1 day")
    .max(999999, "Cannot exceed 999999")
    .required("Tagging period is required"),
});

function ChannelPartners() {
  const { projectId } = useParams();
  const { userInfo } = useUser();
  const { hasPrivilege } = usePrivilege();
  const developerCustomerId =
    userInfo?.loginIdentifierDetails?.customer?.customerId;
  
  // State management
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [project, setProject] = useState(null);
  const [hideCpInventory, setHideCpInventory] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [extensionPeriod, setExtensionPeriod] = useState(30);
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [extensionToggleLoading, setExtensionToggleLoading] = useState(false);
  // Getter unused after the FormSubmit migration (Form drives the spinner);
  // setter still flips during the PUT.
  // eslint-disable-next-line no-unused-vars
  const [_extensionUpdateLoading, setExtensionUpdateLoading] = useState(false);
  const [propertyConfigs, setPropertyConfigs] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedConfigs, setSelectedConfigs] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [agentConfigMap, setAgentConfigMap] = useState({});
  const gridRef = useRef(null);

  // Channel partners for this project from customer relations (secondary customer = partner)
  const fetchAgents = useCallback(async () => {
    try {
      const relationRes = await axiosInstance.get("/customers/relation", {
        params: {
          projectId: projectId != null ? Number(projectId) : undefined,
          page: 1,
          page_size: 1000,
        },
      });

      const rawItems =
        relationRes?.items ||
        (Array.isArray(relationRes) ? relationRes : []);

      const byAgentId = new Map();
      for (const item of rawItems) {
        const sc = item.secondaryCustomer;
        if (!sc?.customerId) continue;
        const id = sc.customerId;
        if (byAgentId.has(id)) continue;

        byAgentId.set(id, {
          ...sc,
        });
      }

      setAgents(Array.from(byAgentId.values()));
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast.error("Failed to fetch channel partners");
    }
  }, [projectId]);

  // Fetch project configurations
  const fetchConfigurations = useCallback(async () => {
    try {
      const configs = await fetchPropertyConfigurations({
        projectId: projectId,
      });

      let configurationList = [];
      if (configs?.items && Array.isArray(configs.items)) {
        configurationList = configs.items;
      } else if (Array.isArray(configs)) {
        configurationList = configs;
      }
      setPropertyConfigs(configurationList);

      const projectData = configurationList[0]?.projectPhase?.project;
      setProject(projectData);
      
      // Set the hideCpInventory state from project configuration
      if (projectData?.configuration?.hideCpInventory !== undefined) {
        setHideCpInventory(projectData.configuration.hideCpInventory);
      }
      
      // Set the extension period from project configuration
      if (projectData?.configuration?.tagDurationDays !== undefined) {
        setExtensionPeriod(projectData.configuration.tagDurationDays);
      }
 
    } catch (error) {
      console.error("Error fetching configurations:", error);
    
    }
  }, [projectId]);

  const fetchAgentConfigMappings = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/customers/agent/project/configs", {
        params: {
          projectId: projectId != null ? Number(projectId) : undefined,
          customerId: developerCustomerId,
          page: 1,
          page_size: 1000,
        },
      });

      const rawItems =
        response?.items || (Array.isArray(response) ? response : []);

      const nextMap = {};
      rawItems.forEach((item) => {
        const agentId = item?.agent?.customerId;
        if (!agentId) return;
        nextMap[agentId] = Array.isArray(item?.propertyConfigs)
          ? item.propertyConfigs
          : [];
      });

      setAgentConfigMap(nextMap);
    } catch (error) {
      console.error("Error fetching agent config mappings:", error);
      setAgentConfigMap({});
    }
  }, [projectId, developerCustomerId]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAgents(),
        fetchConfigurations(),
        fetchAgentConfigMappings(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchAgents, fetchConfigurations, fetchAgentConfigMappings]);

  // Filter agents based on search query
  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return agents;
    
    const query = searchQuery.toLowerCase();
    return agents.filter((agent) => {
      return (
        agent.ownerName?.toLowerCase().includes(query) ||
        agent.companyName?.toLowerCase().includes(query) ||
        agent.emailId?.toLowerCase().includes(query) ||
        agent.mobileNoCalling?.toLowerCase().includes(query) ||
        agent.reraNumber?.toLowerCase().includes(query)
      );
    });
  }, [agents, searchQuery]);

  const projectConfigsForProject = useMemo(
    () =>
      propertyConfigs.filter(
        (c) =>
          String(c?.projectPhase?.project?.projectId) === String(projectId)
      ),
    [propertyConfigs, projectId]
  );

  const openAssignModal = useCallback((agent) => {
    setSelectedAgent(agent);
    const currentConfigs = (agentConfigMap[agent.customerId] || [])
      .filter(
        (config) =>
          String(config.projectPhase?.project?.projectId) === String(projectId)
      )
      .map((config) => config.propertyConfigId);
    setSelectedConfigs(currentConfigs);
    setAssignModalOpen(true);
  }, [agentConfigMap, projectId]);

  const closeAssignModal = useCallback(() => {
    setAssignModalOpen(false);
    setSelectedAgent(null);
    setSelectedConfigs([]);
  }, []);

  const handleConfigSelect = (configId, isChecked) => {
    setSelectedConfigs((prev) => {
      if (isChecked) {
        return [...prev, configId];
      }
      return prev.filter((id) => id !== configId);
    });
  };

  const handleAssign = useCallback(async () => {
    if (!selectedAgent || selectedConfigs.length === 0) {
      toast.warn("Please select at least one configuration");
      return;
    }

    setAssignLoading(true);
    try {
      const developerId = userInfo?.loginIdentifierDetails?.customer?.customerId;

      const payload = {
        agentData: {
          customerId: selectedAgent.customerId,
          companyName: selectedAgent.companyName,
          ownerName: selectedAgent.ownerName,
          mobileNoWa: selectedAgent.mobileNoWa,
          mobileNoCalling: selectedAgent.mobileNoCalling,
          emailId: selectedAgent.emailId,
          emailPasswordSecret: encryptForTransit(selectedAgent.emailPasswordSecret),
          address: selectedAgent.address,
          customerType: "AGENT",
          reraNumber: selectedAgent.reraNumber,
          isActive: selectedAgent.isActive,
          metaId: selectedAgent.metaId,
          whatsappSecretAccessToken: encryptForTransit(
            selectedAgent.whatsappSecretAccessToken
          ),
        },
        propertyConfigIds: selectedConfigs,
        developerId: developerId,
        projectIds: [projectId],
      };

      await axiosInstance.put(
        `/customers/agent/project/configs?agentId=${selectedAgent.customerId}`,
        payload
      );

      toast.success("Agent assigned successfully!");
      closeAssignModal();

      await Promise.all([fetchAgents(), fetchAgentConfigMappings()]);
    } catch (error) {
      console.error("Error assigning agent:", error);
      toast.error(error?.response?.data?.message || "Failed to assign agent");
    } finally {
      setAssignLoading(false);
    }
  }, [
    selectedAgent,
    selectedConfigs,
    userInfo,
    projectId,
    fetchAgents,
    fetchAgentConfigMappings,
    closeAssignModal,
  ]);

  // AG Grid Column Definitions
  const columns = useMemo(() => {
    const cols = [
      { key: "owner", header: "Owner", width: 180, render: (r) => (
        <span className="text-sm font-medium text-slate-900 truncate block" title={r.ownerName}>
          {r.ownerName || "—"}
        </span>
      )},
      { key: "email", header: "Email", width: 220, render: (r) => (
        <span className="text-sm text-slate-700 truncate block" title={r.emailId}>
          {r.emailId || "—"}
        </span>
      )},
      { key: "phone", header: "Phone", width: 140, render: (r) => (
        <span className="text-sm text-slate-700 tabular-nums">{r.mobileNoCalling || "—"}</span>
      )},
      { key: "rera", header: "RERA", width: 130, render: (r) => {
        const rera = r?.reraNumber ?? r?.reraNo ?? r?.rera_number;
        return (
          <span className="text-xs text-slate-700 tabular-nums truncate block" title={rera || ""}>
            {rera && String(rera).trim() ? rera : "—"}
          </span>
        );
      }},
      { key: "status", header: "Status", width: 110, render: (r) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
          r.isActive
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-rose-50 text-rose-700 border-rose-200"
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {r.isActive ? "ACTIVE" : "INACTIVE"}
        </span>
      )},
    ];

    if (hasPrivilege("canEdit")) {
      cols.push({
        key: "actions",
        header: "",
        width: 100,
        align: "center",
        render: (r) => {
          const isEdit = (agentConfigMap[r?.customerId] || []).length > 0;
          return (
            <OutlineButton
              className="!w-auto min-w-0 !py-1 !px-3 text-xs"
              onClick={() => openAssignModal(r)}
            >
              {isEdit ? "Edit" : "Assign"}
            </OutlineButton>
          );
        },
      });
    }

    return cols;
  }, [hasPrivilege, openAssignModal, agentConfigMap]);

  // AG Grid onGridReady handler
  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
    
    const handleResize = () => {
      params.api.sizeColumnsToFit();
    };
    
    window.addEventListener('resize', handleResize);
    params.api.resizeHandler = handleResize;
  };

  // Handle inventory access toggle (enabled => hideCpInventory = false)
  const handleInventoryToggle = async (event, isEnabled) => {
    event.stopPropagation(); // Prevent navigation to new tab
    setToggleLoading(true);
    try {
      
      const configuration = {
        ...project?.configuration,
        hideCpInventory: !isEnabled 
      };

      // Create FormData for the request with all required project fields
      const formData = new FormData();
      formData.append('project_name', project?.projectName || '');
      formData.append('developer', project?.developer || '');
      formData.append('customer_id', project?.customerId || '');
      formData.append('address_building_no', project?.address?.buildingNo || '');
      formData.append('address_street_name1', project?.address?.streetName1 || '');
      formData.append('address_city', project?.address?.city || '');
      formData.append('address_district', project?.address?.district || '');
      formData.append('address_pin_code', project?.address?.pinCode || '');
      formData.append('address_state', project?.address?.state || '');
      formData.append('address_country', project?.address?.country || '');
      formData.append('towers', project?.towers || '');
      formData.append('size_acres', project?.sizeAcres || '');
      formData.append('configuration', JSON.stringify(configuration));

      // Make PUT request to /projects with projectId and loginId as query parameters
      await axiosInstance.put(`/projects?projectId=${projectId}&loginId=${userInfo?.loginId}`, formData)
      // Update local state
      setHideCpInventory(!isEnabled);
      
      // Update project object
      setProject(prev => ({
        ...prev,
        configuration: {
          ...prev?.configuration,
          hideCpInventory: !isEnabled
        }
      }));
      
      toast.success(`Inventory access ${isEnabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error("Error updating inventory visibility:", error);
      toast.error(error?.response?.data?.message || "Failed to update inventory visibility");
    } finally {
      setToggleLoading(false);
    }
  };

  // Handle extension period toggle
  const handleExtensionToggle = async (event, isEnabled) => {
    event.stopPropagation();
    setExtensionToggleLoading(true);
    try {
      const configuration = {
        ...project?.configuration,
        tagDurationDays: isEnabled ? extensionPeriod : 999999
      };

      // Create FormData for the request with all required project fields
      const formData = new FormData();
      formData.append('project_name', project?.projectName || '');
      formData.append('developer', project?.developer || '');
      formData.append('customer_id', project?.customerId || '');
      formData.append('address_building_no', project?.address?.buildingNo || '');
      formData.append('address_street_name1', project?.address?.streetName1 || '');
      formData.append('address_city', project?.address?.city || '');
      formData.append('address_district', project?.address?.district || '');
      formData.append('address_pin_code', project?.address?.pinCode || '');
      formData.append('address_state', project?.address?.state || '');
      formData.append('address_country', project?.address?.country || '');
      formData.append('towers', project?.towers || '');
      formData.append('size_acres', project?.sizeAcres || '');
      formData.append('configuration', JSON.stringify(configuration));

      // Make PUT request to /projects with projectId as query parameter
      await axiosInstance.put(`/projects?projectId=${projectId}`, formData);
      
      // Update project object
      setProject(prev => ({
        ...prev,
        configuration: {
          ...prev?.configuration,
          tagDurationDays: isEnabled ? extensionPeriod : 999999
        }
      }));
      
      toast.success(`Extension period ${isEnabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error("Error updating extension period:", error);
      toast.error(error?.response?.data?.message || "Failed to update extension period");
    } finally {
      setExtensionToggleLoading(false);
    }
  };

  // Handle extension period value update
  const handleExtensionUpdate = async (newValue) => {
    setExtensionUpdateLoading(true);
    try {
      const configuration = {
        ...project?.configuration,
        tagDurationDays: newValue
      };

      // Create FormData for the request with all required project fields
      const formData = new FormData();
      formData.append('project_name', project?.projectName || '');
      formData.append('developer', project?.developer || '');
      formData.append('customer_id', project?.customerId || '');
      formData.append('address_building_no', project?.address?.buildingNo || '');
      formData.append('address_street_name1', project?.address?.streetName1 || '');
      formData.append('address_city', project?.address?.city || '');
      formData.append('address_district', project?.address?.district || '');
      formData.append('address_pin_code', project?.address?.pinCode || '');
      formData.append('address_state', project?.address?.state || '');
      formData.append('address_country', project?.address?.country || '');
      formData.append('towers', project?.towers || '');
      formData.append('size_acres', project?.sizeAcres || '');
      formData.append('configuration', JSON.stringify(configuration));
      

      // Make PUT request to /api/projects with projectId and loginId as query parameters (ensure loginId is parsed as integer)
      await axiosInstance.put(
        `/projects?projectId=${projectId}&loginId=${userInfo?.loginId}`,
        formData
      );

      setExtensionPeriod(newValue);
      
      // Update project object
      setProject(prev => ({
        ...prev,
        configuration: {
          ...prev?.configuration,
          tagDurationDays: newValue
        }
      }));
      
      setExtensionModalOpen(false);
      toast.success("Extension period updated successfully!");
    } catch (error) {
      console.error("Error updating extension period:", error);
      toast.error(error?.response?.data?.message || "Failed to update extension period");
    } finally {
      setExtensionUpdateLoading(false);
    }
  };

  // Export functionality
  const handleExport = () => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: 'channel-partners.csv',
        columnSeparator: ',',
      });
    }
  };

  // Cleanup resize listener
  useEffect(() => {
    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, []);

  return (
    <div className="w-full flex lg:flex-row flex-col gap-4">
      <div className="lg:w-2/3 w-full space-y-4">
        {/* Header card — section title, subtitle, count, search and export
            sit in a single cohesive panel. Same card language as Properties. */}
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-4 lg:p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-base lg:text-lg font-semibold text-slate-900">Channel Partners</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {filteredAgents.length} partner{filteredAgents.length === 1 ? "" : "s"}
                {searchQuery ? ` matching "${searchQuery}"` : " linked to this project"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder="Search by name, company, email, phone or RERA…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-72 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200"
              />
              {hasPrivilege("canEdit") && (
                <OutlineButton onClick={handleExport}>
                  Export CSV
                </OutlineButton>
              )}
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredAgents}
          loading={loading}
          minWidth={0}
          rowKey={(r, i) => r?.customerId ?? i}
          emptyState={
            <div className="px-6 py-12 text-center">
              <p className="text-base font-semibold text-slate-900">No channel partners found</p>
              <p className="text-sm text-slate-500 mt-1">
                Try adjusting your search criteria.
              </p>
            </div>
          }
        />
      </div>

      <div className="lg:w-1/3 w-full flex flex-col gap-4 h-full">
        {/* Channel Partner Settings — single card, matches the rest of the
            project surfaces. */}
        {hasPrivilege("canEdit") && (
          <Card className="rounded-2xl shadow-sm border border-slate-200 p-5">
            <CardBody className="p-0">
              <h3 className="text-base font-semibold text-slate-900 mb-4">Channel Partner Settings</h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Configure channel partner access and extension settings for this project.
              </p>
              
              {/* Inventory Visibility Setting */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Inventory Access</span>
                    <p className="text-xs text-gray-500">
                      Enable channel partners to view project inventory and pricing
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!hideCpInventory}
                      disabled={toggleLoading}
                      onChange={(e) => handleInventoryToggle(e, e.target.checked)}
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${toggleLoading ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                    {toggleLoading && (
                      <div className="absolute right-0 top-0">
                        <Spinner color="blue" size="sm" />
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Extension Period Setting */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Lead Tagging Period</span>
                    <p className="text-xs text-gray-500">
                      Set duration for lead to channel partner tagging
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={extensionPeriod !== 999999}
                      disabled={extensionToggleLoading}
                      onChange={(e) => handleExtensionToggle(e, e.target.checked)}
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${extensionToggleLoading ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                    {extensionToggleLoading && (
                      <div className="absolute right-0 top-0">
                        <Spinner color="blue" size="sm" />
                      </div>
                    )}
                  </label>
                </div>
                
                {extensionPeriod !== 999999 && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-gray-600">
                      Current: {extensionPeriod} days
                    </span>
                    <EditButton
                      size="sm"
                      onClick={() => setExtensionModalOpen(true)}
                    
                    >
                      Edit
                    </EditButton>
                  </div>
                )}
              </div>
              
            </CardBody>
          </Card>
        )}

        {/* Marketing Media Upload */}
        {hasPrivilege("canEdit") && (
          <div>
            {/* <h2 className="text-lg font-semibold text-gray-900 mb-4">Marketing Media</h2> */}
            <AgreementUpload
              projectId={projectId}
              project = {project}
              type="marketing_media"
            />
          </div>
        )}
      </div>

      {/* Edit Extension Period */}
      <FormModal
        open={extensionModalOpen}
        onClose={() => setExtensionModalOpen(false)}
        title="Edit Extension Period"
        size="sm"
      >
        <Form
          key={extensionModalOpen ? "open" : "closed"}
          schema={extensionPeriodSchema}
          defaultValues={{ extensionPeriod }}
          onSubmit={async ({ extensionPeriod: nextValue }) => {
            setExtensionPeriod(nextValue);
            await handleExtensionUpdate(nextValue);
          }}
        >
          <Field
            name="extensionPeriod"
            label="Tagging Period (Days)"
            type="number"
            min={1}
            max={999999}
            placeholder="Enter number of days"
            required
            showInfo
            infoText="Set the number of days for lead-to-channel-partner tagging."
          />
          <FormSubmit loadingLabel="Updating...">Update</FormSubmit>
        </Form>
      </FormModal>

      {/* Assign configurations to a channel partner */}
      <FormModal
        open={assignModalOpen && !!selectedAgent}
        onClose={closeAssignModal}
        title="Assign configurations"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <OutlineButton onClick={closeAssignModal} className="!w-auto">Cancel</OutlineButton>
            <PrimaryButton
              onClick={handleAssign}
              disabled={
                assignLoading ||
                selectedConfigs.length === 0 ||
                projectConfigsForProject.length === 0
              }
            >
              {assignLoading ? <Spinner color="white" size="sm" /> : "Save"}
            </PrimaryButton>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-4">
          {selectedAgent?.ownerName ?? "Partner"} — select property configurations for this project.
        </p>
        <div className="space-y-2">
          {projectConfigsForProject.length === 0 ? (
            <p className="text-sm text-gray-500">
              No configurations found for this project.
            </p>
          ) : (
            projectConfigsForProject.map((config) => (
              <label
                key={config.propertyConfigId}
                className="flex items-center gap-2 cursor-pointer text-sm select-none"
              >
                <input
                  type="checkbox"
                  checked={selectedConfigs.includes(config.propertyConfigId)}
                  onChange={(e) => handleConfigSelect(config.propertyConfigId, e.target.checked)}
                  className="rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
                />
                <span>{config.configName || `Config #${config.propertyConfigId}`}</span>
              </label>
            ))
          )}
        </div>
      </FormModal>
    </div>
  );
}

export default ChannelPartners;